import React, {
  FC,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import { observer } from 'mobx-react';
import { DialogActions, DialogContent, Divider } from '@mui/material';
import Dialog from 'components/UI/Dialog';
import Text from 'components/UI/Text';
import Button from 'components/UI/Button';
import { theme } from 'components/UI';
import { useTranslation } from 'react-i18next';
import { useStores } from 'stores/hooks/hooks';
import { ActionMode, DialogOnCloseReason } from 'models/shared';
import styled from 'styled-components';
import api from 'Api/ApiMethods';
import { GrowerSystem } from 'models/grower';
import Select from 'components/UI/Select';
import { ActionParams, ActionType } from 'models/actions';
import Grid from 'components/UI/Grid';
import { DropdownChangeEvent } from 'primereact/dropdown';
import ActionManagementDialogForm from './ActionManagementDialogForm';

const StyledGrid = styled(Grid)`
  justify-content: unset;
`;

const DialogBody = styled.div`
  display: inline-flex;
  flex-direction: column;
`;

const DialogBodyContainer = styled.div`
  display: flex;
  padding: 1rem 1rem;
  flex-direction: column;
`;

interface ActionManagementDialogProps {
  onHide: () => void;
  visible: boolean;
  title: string;
  mode: ActionMode;
}

const NoWrappingText = styled(Text)`
  white-space: nowrap;
  min-width: 30%;
  max-width: 30%;
  text-align: start;
`;

const emptyParams: ActionParams = {
  systemId: undefined,
  name: '',
  valves: [],
  mode: null,
  value: 0,
  serial: '',
  notifyOnTrigger: true,
};

const ActionManagementDialog: FC<ActionManagementDialogProps> = observer(
  ({ visible = false, onHide, title, mode }) => {
    const { t: growerT } = useTranslation('grower');
    const { t: generalT } = useTranslation('general');
    const { actionsStore, resellersStore } = useStores();
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [schemaData, setSchemaData] = useState<{
      schema: any;
      params: any;
      serials: {
        label: string;
        value: string;
      }[];
    } | null>(null);

    const [eligibleSystems, setEligibleSystems] = useState<GrowerSystem[]>([]);

    const [params, setParams] = useState<ActionParams | null>(null);

    const growerSystems = useMemo(() => {
      return eligibleSystems.map((system) => ({
        label: system.displayName,
        value: system.id as number,
      }));
    }, [eligibleSystems]);

    const [selectedGrowerSystem, setSelectedGrowerSystem] = useState<{
      label: string;
      value: number;
    } | null>(null);

    const paramsIsValid = useMemo(() => {
      const paramsWithValue = !!(
        params?.mode?.mode_Volume ||
        params?.mode?.mode_MM ||
        params?.mode?.mode_Time
      );

      const paramsWithoutValue = !!(
        params?.mode?.mode_Stop || params?.mode?.mode_Virtual
      );

      const actionWithValue = !!(
        params?.mode?.mode_DryContact && params?.mode?.value
      )

      const valueValid =
        (paramsWithValue && params?.mode?.value && params?.mode?.value > 0) ||
        paramsWithoutValue ||
        actionWithValue;

      return (
        params?.name &&
        params?.serial &&
        params?.systemId &&
        params?.mode &&
        valueValid
      );
    }, [params]);

    const closeDialog = useCallback(() => {
      onHide();
    }, [onHide]);

    const handleResetCloseDialog = useCallback(() => {
      // plotsStore.setSelectedMassEditRows([]);
      closeDialog();
    }, [closeDialog]);

    const handleCloseDialog = useCallback(
      (event: unknown, reason: DialogOnCloseReason) => {
        if (reason && reason === DialogOnCloseReason.BackDropClick) {
          return;
        }

        handleResetCloseDialog();
      },
      [handleResetCloseDialog],
    );

    const handleChangeParams = useCallback((newParam: ActionParams) => {
      setParams(newParam);
    }, []);

    const handleSubmitClick = useCallback(async () => {
      setIsSaving(true);
      if (mode === ActionMode.CREATE && params) {
        actionsStore.postAction(params);
      }

      if (mode === ActionMode.EDIT && params) {
        // const actionValue = params?.mode?.value || params?.value;
        const fixedAction = {
          ...params,
          valves: params?.valves || [],
          // valueToDisplay: actionValue ? Number(actionValue).toFixed(3) : '',
        } as unknown as ActionType;

        actionsStore.updateAction(
          fixedAction.id,
          fixedAction.systemId,
          fixedAction,
        );
      }

      setIsSaving(false);
      closeDialog();
    }, [actionsStore, closeDialog, mode, params]);

    const getDataEdit = useCallback(async () => {
      try {
        setIsLoading(true);
        const currentAction = actionsStore.selectedActions?.[0];
        const currentGrower = resellersStore.selectedGrowerId;
        const systems = await api.getGrowerSystems(currentGrower);
        // const systemTypes = await api.getSystemTypes();

        const availableEligibleSystems = systems.filter(
          (system: any) => system.type === 'DREAM',
        );

        const currentSystem = actionsStore.systemsCall.data.find(
          (system) => system.id === currentAction?.systemId,
        );

        if (
          mode === ActionMode.EDIT &&
          currentAction &&
          availableEligibleSystems.length > 0
        ) {
          actionsStore
            .getActionParams(
              currentAction?.id,
              currentAction?.systemId,
              'DREAM',
            )
            .then((data) => {
              const serials = data?.schema?.definitions?.serial?.enum?.map(
                (serial: string, index: number) => ({
                  label:
                    data?.schema?.definitions?.serial?.options?.enum_titles?.[
                    index
                    ],
                  value: serial,
                }),
              );

              setSchemaData({
                schema: data?.schema,
                params: data?.params,
                serials,
              });

              setParams({ ...currentAction, ...data?.params });
              setSelectedGrowerSystem({
                label: currentSystem?.displayName || '',
                value: currentAction?.systemId,
              });

              setEligibleSystems([currentSystem as GrowerSystem]);
            });
        }
      } catch (error) {
        console.log('error', error);
      } finally {
        setIsLoading(false);
      }
    }, [actionsStore, mode, resellersStore.selectedGrowerId]);

    const getEligibleSystems = useCallback(async () => {
      try {
        setIsLoading(true);
        const currentGrower = resellersStore.selectedGrowerId;
        const systems = await api.getGrowerSystems(currentGrower);
        // const systemTypes = await api.getSystemTypes();

        const availableEligibleSystems = systems.filter(
          (system: any) => system.type === 'DREAM',
        );

        setEligibleSystems(availableEligibleSystems);
      } catch (error) {
        console.log('error', error);
      } finally {
        setIsLoading(false);
      }
    }, [resellersStore.selectedGrowerId]);

    const handleChangeSystem = useCallback(
      (e: DropdownChangeEvent) => {
        const foundSystem = growerSystems.find(
          (system) => system.value === e.value,
        );

        if (foundSystem) {
          setParams({ ...emptyParams, systemId: foundSystem.value });

          setSelectedGrowerSystem(foundSystem);
        }
      },
      [growerSystems],
    );

    const getDataSchema = useCallback(
      async (systemId: number) => {
        try {
          setIsLoading(true);
          const selectedSystemData = await api.getActionParams(
            undefined,
            systemId,
            'DREAM',
          );

          const serials =
            selectedSystemData?.schema?.definitions?.serial?.enum?.map(
              (serial: string, index: number) => ({
                label:
                  selectedSystemData?.schema?.definitions?.serial?.options
                    ?.enum_titles?.[index],
                value: serial,
              }),
            );

          setSchemaData({
            schema: selectedSystemData?.schema,
            params: selectedSystemData?.params,
            serials,
          });
        } catch (error) {
          console.log('error', error);
          onHide();
        } finally {
          setIsLoading(false);
        }
      },
      [onHide],
    );

    useEffect(() => {
      if (visible && mode === ActionMode.EDIT) {
        getDataEdit();
      }

      if (visible && mode === ActionMode.CREATE) {
        getEligibleSystems();
      }
    }, [visible, actionsStore, getDataEdit, mode, getEligibleSystems]);

    useEffect(() => {
      if (selectedGrowerSystem?.value && mode === ActionMode.CREATE) {
        getDataSchema(selectedGrowerSystem.value);
      }
    }, [selectedGrowerSystem, getDataSchema, mode]);

    return (
      <Dialog
        open={visible}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        dialogTitle={title}
        titleWeight="bold"
        disableEscapeKeyDown
      >
        <DialogContent>
          <DialogBodyContainer>
            <DialogBody>
              <Grid direction="column" gap="10px">
                <StyledGrid direction="row" gap="10px" alignSelf="start" grow>
                  <NoWrappingText>
                    {growerT('actions_form_system')}:
                  </NoWrappingText>
                  <Select
                    options={growerSystems || []}
                    value={selectedGrowerSystem?.value}
                    onChange={handleChangeSystem}
                    disabled={mode !== ActionMode.CREATE || isLoading}
                    placeholder="Select a system"
                  />
                </StyledGrid>
                {schemaData?.serials && (
                  <ActionManagementDialogForm
                    serials={schemaData?.serials}
                    params={params || emptyParams}
                    handleChangeParams={handleChangeParams}
                    eligibleSystems={eligibleSystems}
                    mode={mode}
                  />
                )}
              </Grid>
            </DialogBody>
          </DialogBodyContainer>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={!paramsIsValid}
            onClick={handleSubmitClick}
            loading={isSaving}
          >
            <Text textcolor={theme.color.white} size="sm">
              {generalT('apply')}
            </Text>
          </Button>
        </DialogActions>
      </Dialog>
    );
  },
);

export default ActionManagementDialog;
